import * as React from 'react'

import { styled } from '~/styled'
import { cn } from '~/styled'

import type { TAvatarRootContext } from './AvatarContext'
import { AvatarRootContext } from './AvatarContext'
import { AvatarIcon } from './AvatarIcon'
import { AvatarImage } from './AvatarImage'
import { AvatarInitial } from './AvatarInitial'
import { AvatarPlaceholder } from './AvatarPlaceholder'

const avatarRootStyles = [
  'flex',
  'justify-center',
  'items-center',
  'text-grey-800',
  'rounded-full',
  'border-2',
  'border-grey-200',
  'bg-(--bg-color)',
  'overflow-hidden'
]

const StyledDiv = styled('div', {
  base: avatarRootStyles,
  variants: {
    size: {
      xs: ['size-6'],
      sm: ['size-8'],
      md: ['size-10'],
      lg: ['size-12'],
      xl: ['size-16'],
      xxl: ['size-24']
    },
    emphasis: {
      bold: ['border-0', '[&>p]:text-white'],
      subtle: ['border-0'],
      white: []
    }
  }
})

const StyledButton = styled('button', {
  base: [
    ...avatarRootStyles,
    'not-disabled:active:bg-grey-200',
    'not-disabled:active:border-primary-800',
    'not-disabled:hover:bg-grey-100',
    'not-disabled:hover:border-grey-800',
    'not-disabled:focus-visible:outline-none',
    'not-disabled:focus-visible:relative',
    'not-disabled:focus-visible:shadow-[var(--color-primary-800)_0px_0px_0px_4px]',
    'not-disabled:focus-visible:shadow-[white_0px_0px_0px_2px]',
    'not-disabled:focus-visible:z-1',
    'disabled:cursor-not-allowed',
    'disabled:opacity-30'
  ],
  variants: {
    size: {
      xs: ['size-6'],
      sm: ['size-8'],
      md: ['size-10'],
      lg: ['size-12'],
      xl: ['size-16'],
      xxl: ['size-24']
    },
    emphasis: {
      bold: ['border-0', '[&>p]:text-white'],
      subtle: ['border-0'],
      white: []
    }
  }
})

type TAvatarProps = React.ComponentProps<
  typeof StyledDiv | typeof StyledButton
> & {
  name?: string
} & (
    | {
        onClick: React.MouseEventHandler<HTMLButtonElement>
        disabled?: boolean
      }
    | { onClick?: never; disabled?: never }
  )

export { AvatarRootContext }
export type { TAvatarRootContext }

export const AvatarRootProvider = ({ children, name, size }: TAvatarProps) => {
  const value = React.useMemo<TAvatarRootContext>(
    () => ({ name, size }),
    [name, size]
  )

  return (
    <AvatarRootContext.Provider value={value}>
      {children}
    </AvatarRootContext.Provider>
  )
}

const DS_COLORS = [
  'maroon',
  'red',
  'magenta',
  'pink',
  'purple',
  'lapis',
  'blue',
  'cyan',
  'teal',
  'green',
  'lime',
  'yellow',
  'orange'
] as const

const getBackgroundColor = ({
  emphasis,
  name
}: Pick<TAvatarProps, 'emphasis' | 'name'>) => {
  if (emphasis === 'white' || !name) return 'white'
  const index = name.toLocaleUpperCase().charCodeAt(0) % DS_COLORS.length
  return `var(--color-${DS_COLORS[index]}-${emphasis === 'bold' ? 700 : 300})`
}

export const AvatarRoot = ({
  children,
  size = 'lg',
  name,
  emphasis = 'white',
  disabled = false,
  onClick
}: TAvatarProps) => {
  const backgroundColor = getBackgroundColor({ emphasis, name })
  return (
    <AvatarRootProvider name={name} size={size}>
      {onClick ? (
        <StyledButton
          size={size}
          style={{
            '--bg-color': backgroundColor
          }}
          disabled={disabled}
          onClick={disabled ? undefined : onClick}
          className={cn(disabled ? 'cursor-auto' : 'cursor-pointer')}
          emphasis={emphasis}
        >
          {children}
        </StyledButton>
      ) : (
        <StyledDiv
          size={size}
          style={{
            '--bg-color': backgroundColor
          }}
          emphasis={emphasis}
        >
          {children}
        </StyledDiv>
      )}
    </AvatarRootProvider>
  )
}

export const Avatar = Object.assign(AvatarRoot, {
  Image: AvatarImage,
  Initial: AvatarInitial,
  Placeholder: AvatarPlaceholder,
  Icon: AvatarIcon
})

AvatarRoot.displayName = 'Avatar'
